/* eslint-disable complexity */
import {requestApi} from '@/utils/httpClient';

export type BillingCycleType = 'monthly' | 'yearly' | 'oneTime';
export type BillingEditionType = 'personal' | 'enterprise';
export type BillingProductType = 'ws' | 'wa';
export type WithholdingFeeAction = 'StartExp' | 'StartNewConfig' | '';

interface IUserHeaderParams {
    userId?: string;
}

function buildUserHeaderOptions(params?: IUserHeaderParams) {
    if (!params?.userId) {
        return undefined;
    }

    return {
        headers: {
            'x-user-id': params.userId
        }
    };
}

export interface IEnterprisePoolCredit {
    total: number;
    remaining: number;
    expire_at?: string;
    expire_remain?: number;
}

/** 企业母账号 quotas 附带的企业总池（仅企业母账号返回） */
export interface IEnterprisePool {
    enterprise_id?: string;
    total_available?: number;
    enterprise_credits?: IEnterprisePoolCredit;
    shared_resource_credits?: IEnterprisePoolCredit;
    other_credits?: IEnterprisePoolCredit;
    enterprise_expire_at?: string;
    seats?: {total: number, occupied: number, available: number};
}

/** 子账号 quotas 附带的席位额度块（仅企业子账号返回） */
export interface ISubUserCredit {
    /** 席位 monthly_quota（管理员分配的额度上限）；-1 表示无上限 */
    quota: number;
    /** 本周期已用量 */
    used: number;
    /** 本周期剩余可用 */
    remaining?: number;
    /** 额度重置时间（RFC3339） */
    refresh_time?: string;
    /** 母账号可用资源点余额 */
    mother_available_credits?: number;
    /** 是否占用企业席位 */
    occupied_seat?: boolean;
}

export interface IBillingOverview {
    subscription_plan?: string;
    subscription_expire_at?: string;
    subscription_credits?: number;
    enterprise_package_credits?: number;
    package_credits?: number;
    package_next_expire_at?: string;
    package_next_expire_remain?: number;
    bonus_credits?: number;
    bonus_next_expire_at?: string;
    bonus_next_expire_remain?: number;
    daily_credit?: number;
    refresh_time?: string;
    recharge_url?: {
        ws?: string;
        wa?: string;
    };
    date?: string;
    auto_renew_status?: 'enabled' | 'enable_by_rule' | 'enable_by_order';
    show_enable_auto_renew_button?: boolean;
    enable_auto_renew_button_text?: string;
    enable_auto_renew_goods_id?: string;
    service_id?: string;
    region?: string;
    service_type?: string;
    /** 子用户：已用资源点 */
    sub_user_credit_remaining?: number;
    /** 子用户：分配额度 */
    sub_user_credit_quota?: number;
    /** 企业母账号总池（母账号 quotas 附带） */
    enterprise_pool?: IEnterprisePool;
    /** 子账号席位额度块（子账号 quotas 附带） */
    sub_user_credit?: ISubUserCredit;
}

export type PointsRecordType = 'income' | 'expense' | 'all' | 'charge' | 'change';
export type PointsRecordDirection = 'allocate' | 'reclaim';

export interface IPointsRecordItem {
    ticket?: string;
    comment?: string;
    product?: string;
    time?: string;
    cost?: number;
    direction?: PointsRecordDirection;
    expire_at?: string;
}

export interface IPointsRecordsData {
    total?: number;
    page?: number;
    page_size?: number;
    next_cursor?: number;
    comment_enums?: string[];
    data?: IPointsRecordItem[];
}

export interface IOrderRecordItem {
    order_id?: string;
    product?: string;
    plan_name?: string;
    plan_display_name?: string;
    operation?: string;
    spec?: string;
    amount?: string;
    status?: string;
    created_at?: string;
    expire_at?: string | null;
}

export interface IOrderRecordsData {
    total?: number;
    page?: number;
    page_size?: number;
    data?: IOrderRecordItem[];
}

export interface ISubscriptionPlanHighlightItem {
    key: string;
    text: string;
    tag: string | null;
}

export interface ISubscriptionPlanFeatureItem {
    icon?: string;
    text: string;
    /** feature 文案尾部可点击的链接（如企业版「立即咨询」跳转官网产品页） */
    link?: string;
}

export interface ISubscriptionPlanItem {
    plan_name?: string;
    goods_id?: string;
    plan_display_name?: string;
    price?: string;
    cycle_display?: string;
    subtitle?: string;
    button_text?: string;
    button_disabled?: boolean;
    highlights?: ISubscriptionPlanHighlightItem[];
    features?: ISubscriptionPlanFeatureItem[];
    old_price_text?: string;
    promo_tag_text?: string;
    active?: boolean;
    active_tag_text?: string;
    billingURl?: string;
    show_cancel_button?: boolean;
    cancel_button_text?: string;
    subscription_expire_at?: string;
    show_enable_auto_renew_button?: boolean;
    enable_auto_renew_button_text?: string;
    enable_auto_renew_mode?: 'rule' | 'order';
    region: string;
    service_id: string;
    service_type: string;
}

export interface ISubscriptionCycleOptionItem {
    cycle: BillingCycleType;
    label: string;
    discount?: string;
}

export interface ISubscriptionPlansData {
    cycle_options?: ISubscriptionCycleOptionItem[];
    plans?: ISubscriptionPlanItem[];
}

export interface IPointsPackageItem {
    goods_id?: string;
    plan_name?: string;
    plan_display_name?: string;
    credits?: number;
    validity_days: number;
    monthly_price: string;
    package_price: string;
}

export interface IPointsCheckoutResult {
    order_id?: string;
    pay_url?: string;
}

export interface ISubscriptionCheckoutResult {
    order_id?: string;
    pay_url?: string;
}

export interface IDefaultSubscriptionPlansData {
    estimatedCost: number;
    availableCredits: number;
    userLevel: string;
    rechargeUrl: string;
}

export interface IWithholdingFeeData {
    estimatedCost: number;
    availableCredits: number;
    userLevel: string;
    rechargeUrl: string;
    /** 实际消耗资源点 */
    consumedCredit?: number | null;
    /** 已退回资源点 */
    refundedCredit?: number | null;
    /** 过期流量包资源点 */
    voidCredit?: number | null;
    /** 跨日沉没资源点 */
    sunkCredit?: number | null;
    /** 冷冻资源点 */
    frozenCredits?: number | null;
    /** 轮次变化信息（启用新配置时返回） */
    iteration_change?: {
        current: number;
        next: number;
    } | null;
    /** 原始最大演化轮次 */
    prevMaxIterations?: number;
    /** 当前最大演化轮次 */
    currentMaxIterations?: number;
    /** 缺口资源点 */
    shortfallCredits?: number;
    /** 轮次差额资源点 */
    accumulationCredits?: number;
}

export interface IAccountCreditsItem {
    plan_name?: string;
    total?: number;
    remain?: number;
    expire_at?: string;
}

export interface IAccountInfoData {
    user_id?: string;
    user_name?: string;
    subscription_plan?: string;
    refresh_time?: string;
    daily_credit?: number;
    recharge_url?: {
        ws?: string;
        wa?: string;
    };
    credits_breakdown?: {
        subscription?: IAccountCreditsItem;
        package?: IAccountCreditsItem[];
        gifted?: IAccountCreditsItem[];
    };
}

export interface IContactSubmitParams {
    industry: string;
    contact_name: string;
    contact_info: string;
    company_name: string;
    scenario: string;
}

export function fetchBillingOverview(params?: {
    userId?: string;
    date?: string;
}): Promise<ApiResponse> {
    const queryParams = params?.date ? {date: params.date} : undefined;
    return requestApi.get(
        'console/v1/charge/quotas',
        queryParams,
        buildUserHeaderOptions(params)
    );
}

export function fetchPointsRecords(params?: {
    userId?: string;
    sources?: string[];
    comments?: string[];
    ticket?: string;
    type?: PointsRecordType;
    start_date?: string;
    end_date?: string;
    cursor?: number;
    page?: number;
    page_size?: number;
}): Promise<ApiResponse> {
    const requestParams = {
        ...(params?.sources?.length ? {sources: params.sources.join(',')} : {}),
        ...(params?.comments?.length ? {comments: params.comments.join(',')} : {}),
        ...(params?.ticket ? {ticket: params.ticket} : {}),
        ...(params?.type && params.type !== 'all' ? {type: params.type} : {}),
        ...(params?.start_date ? {start_date: params.start_date} : {}),
        ...(params?.end_date ? {end_date: params.end_date} : {}),
        ...(params?.cursor ? {cursor: params.cursor} : {}),
        ...(params?.page ? {page: params.page} : {}),
        ...(params?.page_size ? {page_size: params.page_size} : {})
    };

    return requestApi.get(
        'console/v1/charge/bills',
        Object.keys(requestParams).length ? requestParams : undefined,
        buildUserHeaderOptions(params)
    );
}

export function fetchOrderRecords(params?: {
    userId?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
}): Promise<ApiResponse> {
    const requestParams = {
        ...(params?.start_date ? {start_date: params.start_date} : {}),
        ...(params?.end_date ? {end_date: params.end_date} : {}),
        ...(params?.page ? {page: params.page} : {}),
        ...(params?.page_size ? {page_size: params.page_size} : {})
    };

    return requestApi.get(
        'console/v1/charge/orders',
        Object.keys(requestParams).length ? requestParams : undefined,
        buildUserHeaderOptions(params)
    );
}

export function fetchSubscriptionPlans(params: {
    userId?: string;
    edition: BillingEditionType;
    cycle?: BillingCycleType;
}): Promise<ApiResponse> {
    const queryParams = {
        edition: params.edition,
        ...(params.cycle ? {cycle: params.cycle} : {})
    };

    return requestApi.get(
        'console/v1/charge/subscriptions/plans',
        queryParams,
        buildUserHeaderOptions(params)
    );
}

export function createSubscriptionOrder(params: {
    userId?: string;
    goods_id: string;
    quantity?: number;
    product?: BillingProductType;
    order_type?: BillingCycleType;
}): Promise<ApiResponse> {
    return requestApi.post(
        'console/v1/charge/orders',
        {
            goods_id: params.goods_id,
            quantity: params.quantity || 1,
            product: params.product || 'ws',
            // 仅在传入时携带 order_type（订阅套餐传周期；资源点包购买不传）
            ...(params.order_type ? {order_type: params.order_type} : {})
        },
        buildUserHeaderOptions(params)
    );
}

/** 创建订单返回：qrcode=true 前端渲染二维码，false 直接跳转 */
export interface ICreateOrderData {
    order_id: string;
    pay_url: string;
    qrcode?: boolean;
}

/** 订单支付状态 */
export type PayStatus = 'pending' | 'paid' | 'expired' | 'failed';

export interface IOrderStatusData {
    order_id: string;
    goods_id?: string;
    product?: string;
    order_type?: string;
    /** 原始订单状态，如 NEED_PURCHASE / CREATED / EXPIRED */
    status?: string;
    /** 归一化支付状态 */
    pay_status: PayStatus;
    paid: boolean;
    expired: boolean;
    can_pay: boolean;
    /** 仍可支付时返回支付地址 */
    pay_url?: string;
    price?: string;
    create_time?: string;
    pay_time?: string;
}

/** 查询订单支付状态（前端轮询用） */
export function getOrderStatus(params: {
    userId?: string;
    orderId: string;
}): Promise<ApiResponse<IOrderStatusData>> {
    return requestApi.get(
        `console/v1/charge/orders/${params.orderId}/status`,
        undefined,
        buildUserHeaderOptions(params)
    );
}

export function fetchPointsPackages(params?: {
    userId?: string;
}): Promise<ApiResponse> {
    return requestApi.get('console/v1/charge/packages/plans', undefined, buildUserHeaderOptions(params));
}

export function submitEnterpriseContact(
    params: IContactSubmitParams & {userId?: string}
): Promise<ApiResponse> {
    const {userId, ...body} = params;
    return requestApi.post(
        'console/v1/charge/contact/submit',
        body,
        buildUserHeaderOptions({userId})
    );
}

export function defaultSubscriptionPlans(): Promise<ApiResponse> {
    return requestApi.get('console/v1/bill/default_subscription_plans');
}

export function fetchWithholdingFee(params: {
    userId?: string;
    job_id: string;
    action?: WithholdingFeeAction;
    iteration?: number;
}): Promise<ApiResponse> {
    const {action, ...restParams} = params;
    let url = 'console/v1/bill/withholding_fee';
    if (action) {
        url += `?action=${action}`;
    }
    return requestApi.post(url, restParams);
}

export function fetchChargeAccountInfo(params: {
    userId: string;
    date?: string;
}): Promise<ApiResponse> {
    const queryParams = params.date ? {date: params.date} : undefined;
    return requestApi.get(
        'console/v1/charge/account_info',
        queryParams,
        buildUserHeaderOptions(params)
    );
}

export function cancelAutoRenewApi(params: {
    serviceId: string;
    serviceType: string;
    region: string;
}): Promise<ApiResponse> {
    return requestApi.post(
        'console/v1/charge/users/auto-renew/cancel',
        params
    );
}

export function enableAutoRenewApi(params: {
    serviceId: string;
    serviceType: string;
    region: string;
}): Promise<ApiResponse> {
    return requestApi.post(
        'console/v1/charge/users/auto-renew/enable',
        params
    );
}

